import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { preloadVisita, getVisitaMeta, clearAllOffline, preloadProductImages, countOfflineImages } from "@/lib/offlineStore";
import { getAutoPreloadConfig, setAutoPreloadConfig, requestNotificationPermission } from "@/lib/autoPreload";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MapPin, Download, CheckCircle2, WifiOff, Trash2, Loader2, Clock, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

function _edadDesde(iso) {
  if (!iso) return "";
  try {
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
    if (diff === 0) return "hoy";
    if (diff === 1) return "hace 1 día";
    if (diff < 7) return `hace ${diff} días`;
    if (diff < 30) return `hace ${Math.floor(diff / 7)} semana${Math.floor(diff / 7) > 1 ? "s" : ""}`;
    return `hace ${Math.floor(diff / 30)} mes${Math.floor(diff / 30) > 1 ? "es" : ""}`;
  } catch { return ""; }
}

/**
 * PrepararVisitaModal — permite al vendedor descargar TODO lo necesario para trabajar
 * offline en las ciudades donde va a estar los próximos días.
 *
 * Descarga: clientes de esas ciudades + productos activos + últimos precios por cliente.
 * Guarda todo en IndexedDB. El PedidoBuilder cae automáticamente a este cache si detecta offline.
 */
export default function PrepararVisitaModal({ open, onOpenChange }) {
  const [departamentos, setDepartamentos] = useState([]);
  const [selectedDepartamentos, setSelectedDepartamentos] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingFotos, setDownloadingFotos] = useState(false);
  const [fotosCount, setFotosCount] = useState(0);
  const [meta, setMeta] = useState(null);
  const [descargarTodo, setDescargarTodo] = useState(false);
  const [autoCfg, setAutoCfg] = useState({ enabled: false, hour: "06:00", frecuencia: "diaria" });

  useEffect(() => {
    if (!open) return;
    setAutoCfg(getAutoPreloadConfig());
    (async () => {
      setLoading(true);
      try {
        const [{ data }, m] = await Promise.all([
          api.get("/pedidos/_/zonas-vendedor"),
          getVisitaMeta(),
        ]);
        setDepartamentos(data?.departamentos || []);
        setMeta(m);
        setFotosCount(await countOfflineImages());
        if (m?.departamentos?.length) setSelectedDepartamentos(new Set(m.departamentos));
      } catch (e) {
        toast.error("No se pudieron cargar los departamentos");
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const toggleDepartamento = (d) => {
    const next = new Set(selectedDepartamentos);
    if (next.has(d)) next.delete(d); else next.add(d);
    setSelectedDepartamentos(next);
  };

  const descargar = async () => {
    if (!descargarTodo && selectedDepartamentos.size === 0) {
      toast.error("Selecciona al menos un departamento o marca 'Todos mis clientes'");
      return;
    }
    setDownloading(true);
    try {
      const body = descargarTodo ? {} : {
        departamentos: Array.from(selectedDepartamentos),
      };
      const { data } = await api.post("/pedidos/_/precargar-visita", body);
      const savedMeta = await preloadVisita(data);
      setMeta(savedMeta);
      // Guardamos aparte los productos con foto para el paso 2 (descargar fotos)
      window.__beemPrepFotos = (data.productos || []).filter((p) => p.tiene_imagen);
      const nextAuto = {
        ...autoCfg,
        departamentos: descargarTodo ? null : Array.from(selectedDepartamentos),
      };
      setAutoPreloadConfig(nextAuto);
      setAutoCfg(nextAuto);
      toast.success(`Listo. ${data.meta.clientes_count} clientes · ${data.meta.productos_count} productos · ${data.meta.precios_entries} precios guardados`);
    } catch (e) {
      toast.error("No se pudo descargar. Revisa tu conexión.");
    } finally {
      setDownloading(false);
    }
  };

  const descargarFotos = async () => {
    setDownloadingFotos(true);
    try {
      let productos = window.__beemPrepFotos;
      if (!productos || !productos.length) {
        const body = descargarTodo ? {} : {
          departamentos: Array.from(selectedDepartamentos),
        };
        const { data } = await api.post("/pedidos/_/precargar-visita", body);
        productos = (data.productos || []).filter((p) => p.tiene_imagen);
      }
      if (!productos.length) {
        toast.info("Ninguno de los productos tiene foto todavía.");
        return;
      }
      const res = await preloadProductImages(API, productos);
      setFotosCount(await countOfflineImages());
      toast.success(`Fotos listas: ${res.ok / 2 | 0} productos guardados${res.fail ? ` (${res.fail} fallaron)` : ""}.`);
    } catch (e) {
      toast.error("No se pudieron descargar las fotos. Revisa tu conexión.");
    } finally {
      setDownloadingFotos(false);
    }
  };

  const limpiar = async () => {
    if (!window.confirm("¿Borrar los datos offline descargados? Necesitarás internet para volver a cargarlos.")) return;
    await clearAllOffline();
    setMeta(null);
    toast.success("Datos offline eliminados");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <WifiOff className="w-5 h-5 text-primary" /> Preparar visita sin internet
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Descarga a tu celular los <b>clientes</b>, <b>productos</b> y los <b>últimos precios</b> pactados
            con cada uno. Podrás armar pedidos aunque no haya señal en el local.
          </p>

          {meta && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm">
              <div className="flex items-center gap-2 font-semibold text-emerald-900">
                <CheckCircle2 className="w-4 h-4" /> Datos disponibles offline
              </div>
              <div className="text-xs text-emerald-800 mt-1 grid grid-cols-3 gap-2">
                <div><b>{meta.clientes_count || 0}</b> clientes</div>
                <div><b>{meta.productos_count || 0}</b> productos</div>
                <div><b>{meta.precios_entries || 0}</b> precios</div>
              </div>
              {(meta.departamentos?.length > 0) && (
                <div className="text-[11px] text-emerald-700 mt-1">
                  <span>Departamentos: {meta.departamentos.join(", ")}</span>
                </div>
              )}
              <div className="text-[11px] text-emerald-700 mt-1">
                Descargado: {meta.at ? new Date(meta.at).toLocaleString() : "-"}
                {meta.at && <span className="ml-1 opacity-80">· {_edadDesde(meta.at)}</span>}
              </div>
              <p className="text-[11px] text-emerald-700/80 mt-1 italic">
                Estos datos se quedan en tu celular. Duran hasta que los borres o hagas otra descarga (una semana, un mes, sin problema).
              </p>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold flex items-center gap-1">
                <MapPin className="w-4 h-4 text-primary" /> ¿En qué departamentos vas a trabajar?
              </div>
              <label className="text-xs flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={descargarTodo}
                  onChange={(e) => setDescargarTodo(e.target.checked)}
                  data-testid="descargar-todo-check"
                />
                Todos mis clientes
              </label>
            </div>

            {loading ? (
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" /> Cargando departamentos…
              </div>
            ) : (departamentos.length === 0) ? (
              <p className="text-xs text-muted-foreground p-2 bg-amber-50 border border-amber-200 rounded">
                Tus clientes aún no tienen departamento configurado. Marca "Todos mis clientes" para descargar todo, o edita las fichas de tus clientes para asignarles el departamento correspondiente.
              </p>
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto p-1">
                <div className="grid grid-cols-2 gap-1.5">
                  {departamentos.map(({ value, count }) => {
                    const active = selectedDepartamentos.has(value);
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => toggleDepartamento(value)}
                        disabled={descargarTodo}
                        className={`text-left text-xs px-2 py-1.5 rounded-md border transition-colors flex justify-between items-center ${
                          active ? "bg-primary/10 border-primary text-primary font-medium" : "bg-background border-border hover:border-primary/40"
                        } ${descargarTodo ? "opacity-50 cursor-not-allowed" : ""}`}
                        data-testid={`departamento-chip-${value}`}
                      >
                        <span>{value}</span>
                        <span className="text-[10px] opacity-70">{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {!descargarTodo && (selectedDepartamentos.size > 0) && (
              <div className="text-[11px] text-muted-foreground mt-1">
                {selectedDepartamentos.size} departamento(s) seleccionado(s)
              </div>
            )}
          </div>

          <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={autoCfg.enabled}
                onChange={async (e) => {
                  const enabled = e.target.checked;
                  const next = { ...autoCfg, enabled };
                  setAutoCfg(next);
                  setAutoPreloadConfig(next);
                  if (enabled) {
                    // Pedir permiso de notificación nativa para avisar cuando se descargue
                    const perm = await requestNotificationPermission();
                    if (perm === "granted") {
                      toast.success(`Programada a las ${next.hour}. Te avisaremos cuando esté lista.`);
                    } else {
                      toast.success(`Programada a las ${next.hour}. Activa las notificaciones para saber cuándo se descargue.`);
                    }
                  }
                }}
                data-testid="autopreload-enabled"
              />
              <span className="text-sm font-semibold flex items-center gap-1"><Clock className="w-4 h-4 text-primary" /> Descargar automáticamente cada mañana</span>
            </label>
            {autoCfg.enabled && (
              <div className="pl-6 space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-16">Cada</span>
                  <select
                    value={autoCfg.frecuencia || "diaria"}
                    onChange={(e) => {
                      const next = { ...autoCfg, frecuencia: e.target.value };
                      setAutoCfg(next);
                      setAutoPreloadConfig(next);
                    }}
                    className="text-xs border border-border rounded-md px-2 py-1 bg-background"
                    data-testid="autopreload-frecuencia"
                  >
                    <option value="diaria">Todos los días</option>
                    <option value="semanal">Cada semana (lunes)</option>
                    <option value="quincenal">Cada 15 días</option>
                    <option value="mensual">Una vez al mes</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-16">A las</span>
                  <input
                    type="time"
                    value={autoCfg.hour}
                    onChange={(e) => {
                      const next = { ...autoCfg, hour: e.target.value };
                      setAutoCfg(next);
                      setAutoPreloadConfig(next);
                    }}
                    className="text-xs border border-border rounded-md px-2 py-1 bg-background"
                    data-testid="autopreload-hour"
                  />
                  <span className="text-[11px] text-muted-foreground">
                    {descargarTodo ? "· todos tus clientes" : (selectedDepartamentos.size > 0 ? `· ${selectedDepartamentos.size} depto(s)` : "· sin filtro")}
                  </span>
                </div>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground pl-6">
              La descarga anterior queda vigente hasta la próxima actualización. Puedes trabajar sin señal días o semanas si no la borras.
            </p>
          </div>

          {/* Paso 2: fotos offline (solo aparece si ya se descargaron datos base) */}
          {meta && (
            <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-primary" /> Descargar fotos para trabajar sin internet
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {fotosCount > 0
                      ? <>Ya tienes <b className="text-emerald-700 tabular-nums">{fotosCount}</b> imágenes guardadas.</>
                      : <>Sin fotos en tu teléfono todavía. Cuando estés sin señal se verán en gris.</>
                    }
                    <br />
                    <span className="text-[11px] italic">Baja miniaturas livianas (~15-40 KB c/u). No usa datos móviles después.</span>
                  </div>
                </div>
                <Button
                  onClick={descargarFotos}
                  disabled={downloadingFotos}
                  size="lg"
                  className="h-11 bg-primary hover:bg-primary/90 shadow-md"
                  data-testid="preparar-visita-descargar-fotos"
                >
                  {downloadingFotos ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImageIcon className="w-4 h-4 mr-2" />}
                  {downloadingFotos ? "Descargando fotos…" : (fotosCount > 0 ? "Actualizar fotos" : "Descargar fotos")}
                </Button>
              </div>
            </div>
          )}

          <div className="flex justify-between gap-2 pt-2 border-t">
            {meta ? (
              <Button variant="ghost" size="sm" onClick={limpiar} className="text-rose-600 hover:text-rose-700">
                <Trash2 className="w-3 h-3 mr-1" /> Borrar cache
              </Button>
            ) : <div />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
              <Button
                onClick={descargar}
                disabled={downloading}
                className="bg-primary hover:bg-primary/90"
                data-testid="preparar-visita-descargar"
              >
                {downloading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                {downloading ? "Descargando…" : (meta ? "Actualizar" : "Descargar")}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
